import {
  sanitizeDiagnosticPath,
  sanitizeDiagnosticText,
  scrubEntry,
} from "@/lib/diagnostics-redaction";

export { sanitizeDiagnosticPath, sanitizeDiagnosticText };

export type DiagnosticLevel = "warning" | "error";
export type DiagnosticSource = "api" | "console" | "browser";

export interface DiagnosticEntry {
  id: number;
  timestamp: string;
  level: DiagnosticLevel;
  source: DiagnosticSource;
  message: string;
  details?: string;
  path?: string;
  status?: number;
  code?: string;
  requestId?: string;
  /** The AUTAR-XXXX-XXXX handle a user quotes to support. */
  supportReference?: string;
  page: string;
  user: string;
  buildVersion: string;
  buildBranch: string;
}

export interface TelemetryEntry {
  id: number;
  timestamp: string;
  event: string;
  properties: string;
  page: string;
  user: string;
  buildVersion: string;
  buildBranch: string;
}

type DiagnosticInput = Omit<
  DiagnosticEntry,
  "id" | "timestamp" | "page" | "user" | "buildVersion" | "buildBranch"
> &
  Partial<
    Pick<
      DiagnosticEntry,
      "timestamp" | "page" | "user" | "buildVersion" | "buildBranch"
    >
  >;
const MAX_DIAGNOSTICS = 200;
const MAX_TELEMETRY = 500;
const DIAGNOSTICS_STORAGE_KEY = "autar:admin-diagnostics:v1";
const listeners = new Set<() => void>();
let diagnostics: DiagnosticEntry[] = [];
let telemetry: TelemetryEntry[] = [];
let nextId = 1;
let channel: BroadcastChannel | null = null;
let diagnosticUser = "unknown";
let hydrated = false;

declare const __CLIENT_BUILD_VERSION__: string;
declare const __CLIENT_BUILD_BRANCH__: string;

function captureContext() {
  return {
    page:
      typeof window === "undefined"
        ? "unknown"
        : sanitizeDiagnosticPath(window.location.pathname),
    user: diagnosticUser,
    buildVersion:
      typeof __CLIENT_BUILD_VERSION__ === "string"
        ? __CLIENT_BUILD_VERSION__
        : "unknown",
    buildBranch:
      typeof __CLIENT_BUILD_BRANCH__ === "string"
        ? __CLIENT_BUILD_BRANCH__
        : "unknown",
  };
}

export function setDiagnosticUser(user: string | null | undefined): void {
  diagnosticUser = user ? sanitizeDiagnosticText(user) : "unknown";
}

type DiagnosticChannelMessage =
  | { type: "diagnostic"; entry: DiagnosticEntry }
  | { type: "telemetry"; entry: TelemetryEntry }
  | { type: "snapshot-request" }
  | {
      type: "snapshot";
      diagnostics: DiagnosticEntry[];
      telemetry: TelemetryEntry[];
    }
  | { type: "clear"; buffer: "diagnostics" | "telemetry" };

function notifyListeners(): void {
  listeners.forEach((listener) => listener());
}

function persistSnapshots(): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(
      DIAGNOSTICS_STORAGE_KEY,
      JSON.stringify({ diagnostics, telemetry }),
    );
  } catch {
    // Diagnostics must never interfere with the application when storage is
    // unavailable, full, or disabled by the browser.
  }
}

function entryKey(entry: DiagnosticEntry | TelemetryEntry): string {
  return "event" in entry
    ? `${entry.timestamp}:telemetry:${entry.event}:${entry.properties}`
    : `${entry.timestamp}:${entry.level}:${entry.source}:${entry.message}:${entry.requestId ?? ""}`;
}

function mergeSnapshots(
  incomingDiagnostics: DiagnosticEntry[],
  incomingTelemetry: TelemetryEntry[],
): void {
  const diagnosticKeys = new Set(diagnostics.map(entryKey));
  const telemetryKeys = new Set(telemetry.map(entryKey));
  const newDiagnostics = incomingDiagnostics
    .map(scrubEntry)
    .filter((entry) => !diagnosticKeys.has(entryKey(entry)))
    .map((entry) => ({ ...entry, id: nextId++ }));
  const newTelemetry = incomingTelemetry
    .map(scrubEntry)
    .filter((entry) => !telemetryKeys.has(entryKey(entry)))
    .map((entry) => ({ ...entry, id: nextId++ }));
  if (!newDiagnostics.length && !newTelemetry.length) return;
  diagnostics = [...diagnostics, ...newDiagnostics]
    .sort((a, b) => a.timestamp.localeCompare(b.timestamp))
    .slice(-MAX_DIAGNOSTICS);
  telemetry = [...telemetry, ...newTelemetry]
    .sort((a, b) => a.timestamp.localeCompare(b.timestamp))
    .slice(-MAX_TELEMETRY);
  persistSnapshots();
  notifyListeners();
}

function hydrateSnapshots(): void {
  if (hydrated || typeof localStorage === "undefined") return;
  hydrated = true;
  try {
    const stored = JSON.parse(
      localStorage.getItem(DIAGNOSTICS_STORAGE_KEY) ?? "{}",
    ) as Partial<{
      diagnostics: DiagnosticEntry[];
      telemetry: TelemetryEntry[];
    }>;
    mergeSnapshots(stored.diagnostics ?? [], stored.telemetry ?? []);
    // Rewrite the store even when nothing merged, so raw entries a previous
    // build saved do not stay on disk.
    if (localStorage.getItem(DIAGNOSTICS_STORAGE_KEY) !== null)
      persistSnapshots();
  } catch {
    try {
      localStorage.removeItem(DIAGNOSTICS_STORAGE_KEY);
    } catch {
      // Ignore storage providers that reject both reads and writes.
    }
  }
}

function ensureChannel(): BroadcastChannel | null {
  hydrateSnapshots();
  if (channel || typeof BroadcastChannel === "undefined") return channel;
  channel = new BroadcastChannel("autar-admin-diagnostics");
  channel.addEventListener(
    "message",
    (event: MessageEvent<DiagnosticChannelMessage>) => {
      const message = event.data;
      if (message.type === "diagnostic") mergeSnapshots([message.entry], []);
      else if (message.type === "telemetry")
        mergeSnapshots([], [message.entry]);
      else if (message.type === "snapshot-request") {
        channel?.postMessage({ type: "snapshot", diagnostics, telemetry });
      } else if (message.type === "snapshot") {
        mergeSnapshots(message.diagnostics, message.telemetry);
      } else if (message.type === "clear") {
        if (message.buffer === "diagnostics") diagnostics = [];
        else telemetry = [];
        persistSnapshots();
        notifyListeners();
      }
    },
  );
  return channel;
}

function appendDiagnostic(input: DiagnosticInput): DiagnosticEntry {
  const entry = scrubEntry<DiagnosticEntry>({
    ...captureContext(),
    ...input,
    id: nextId++,
    timestamp: input.timestamp ?? new Date().toISOString(),
  });
  diagnostics = [...diagnostics.slice(-(MAX_DIAGNOSTICS - 1)), entry];
  persistSnapshots();
  notifyListeners();
  return entry;
}

function appendTelemetry(input: Omit<TelemetryEntry, "id">): TelemetryEntry {
  const entry = scrubEntry<TelemetryEntry>({
    ...captureContext(),
    ...input,
    id: nextId++,
  });
  telemetry = [...telemetry.slice(-(MAX_TELEMETRY - 1)), entry];
  persistSnapshots();
  notifyListeners();
  return entry;
}

export function captureDiagnostic(input: DiagnosticInput): void {
  const entry = appendDiagnostic(input);
  ensureChannel()?.postMessage({ type: "diagnostic", entry });
}

export function captureTelemetry(
  event: string,
  properties: Record<string, unknown>,
): void {
  const entry = appendTelemetry({
    ...captureContext(),
    timestamp: new Date().toISOString(),
    event,
    properties: sanitizeDiagnosticText(properties),
  });
  ensureChannel()?.postMessage({ type: "telemetry", entry });
}

export function formatDiagnosticReport(entry: DiagnosticEntry): string {
  return JSON.stringify(
    {
      report: "Autar diagnostic event",
      dateTime: entry.timestamp,
      severity: entry.level,
      source: entry.source,
      page: entry.page,
      user: entry.user,
      build: {
        branch: entry.buildBranch,
        version: entry.buildVersion,
      },
      http: entry.status
        ? {
            status: entry.status,
            code: entry.code,
            requestId: entry.requestId,
            supportReference: entry.supportReference,
          }
        : undefined,
      message: entry.message,
      details: entry.details,
      path: entry.path,
    },
    null,
    2,
  );
}

export function formatTelemetryReport(entry: TelemetryEntry): string {
  return JSON.stringify(
    {
      report: "Autar telemetry event",
      dateTime: entry.timestamp,
      event: entry.event,
      page: entry.page,
      user: entry.user,
      build: {
        branch: entry.buildBranch,
        version: entry.buildVersion,
      },
      properties: entry.properties,
    },
    null,
    2,
  );
}

export function clearDiagnostics(): void {
  diagnostics = [];
  persistSnapshots();
  notifyListeners();
  ensureChannel()?.postMessage({ type: "clear", buffer: "diagnostics" });
}

export function clearTelemetry(): void {
  telemetry = [];
  persistSnapshots();
  notifyListeners();
  ensureChannel()?.postMessage({ type: "clear", buffer: "telemetry" });
}

/**
 * Drop every captured event and erase the persisted store. Called on sign-out
 * so a shared browser keeps no page paths or user context from the session that
 * just ended.
 */
export function clearDiagnosticsStore(): void {
  diagnostics = [];
  telemetry = [];
  if (typeof localStorage !== "undefined") {
    try {
      localStorage.removeItem(DIAGNOSTICS_STORAGE_KEY);
    } catch {
      // A storage provider that rejects writes has nothing to erase.
    }
  }
  notifyListeners();
  const open = ensureChannel();
  open?.postMessage({ type: "clear", buffer: "diagnostics" });
  open?.postMessage({ type: "clear", buffer: "telemetry" });
}

export function getDiagnosticsSnapshot(): readonly DiagnosticEntry[] {
  hydrateSnapshots();
  return diagnostics;
}

export function getTelemetrySnapshot(): readonly TelemetryEntry[] {
  hydrateSnapshots();
  return telemetry;
}

export function subscribeDiagnostics(listener: () => void): () => void {
  listeners.add(listener);
  ensureChannel()?.postMessage({ type: "snapshot-request" });
  return () => listeners.delete(listener);
}

export function installBrowserDiagnostics(): () => void {
  const originalWarn = console.warn;
  const originalError = console.error;
  const captureConsole = (level: DiagnosticLevel, values: unknown[]) =>
    captureDiagnostic({
      level,
      source: "console",
      message: values.map(sanitizeDiagnosticText).join(" "),
    });
  console.warn = (...values: unknown[]) => {
    captureConsole("warning", values);
    originalWarn(...values);
  };
  console.error = (...values: unknown[]) => {
    captureConsole("error", values);
    originalError(...values);
  };
  const onError = (event: ErrorEvent) =>
    captureDiagnostic({
      level: "error",
      source: "browser",
      message: event.message || "Uncaught browser error",
      details: event.error ? sanitizeDiagnosticText(event.error) : undefined,
    });
  const onRejection = (event: PromiseRejectionEvent) =>
    captureDiagnostic({
      level: "error",
      source: "browser",
      message: "Unhandled promise rejection",
      details: sanitizeDiagnosticText(event.reason),
    });
  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onRejection);
  return () => {
    console.warn = originalWarn;
    console.error = originalError;
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onRejection);
  };
}
